// Offline support for POS (Kasir) — catalog cache + transaction queue
import { isMasterView, getActiveStore } from "@/lib/scope";

const KEY_Q = "pos_offline_txs";
const KEY_P = "pos_cache_products";
const KEY_C = "pos_cache_categories";
const KEY_S = "pos_settings";
// Origin isolation is supplied by localStorage; add cashier/tenant/branch scope.
const scoped = k => {
  if (k === KEY_Q) return k; // Legacy queue is preserved, never silently migrated.
  try {
    const claims = JSON.parse(atob(localStorage.getItem('pos_token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return `${k}:v2:${claims.aud}:${claims.sub}:${getActiveStore()}`;
  } catch { return `${k}:unverified`; }
};

const read = (k, fallback = null) => {
  if (isMasterView()) return fallback;
  try { const v = localStorage.getItem(scoped(k)); return v ? JSON.parse(v) : fallback; } catch { return fallback; }
};
const write = (k, v) => { if (isMasterView()) return; try { localStorage.setItem(scoped(k), JSON.stringify(v)); } catch { /* Optional cache only; shift journal writes never swallow errors. */ } };
export const cachePaymentMethods = methods => write('pos_payment_methods', methods);
export const readPaymentMethods = () => read('pos_payment_methods', []);

// ---- Catalog cache ----
export const cacheCatalog = (products, categories) => {
  if (products) write(KEY_P, products);
  if (categories) write(KEY_C, categories);
};
export const cacheProductsOnly = (products) => write(KEY_P, products);
export const readCachedProducts = () => read(KEY_P, null);
export const readCachedCategories = () => read(KEY_C, []);

// ---- Settings cache (for receipt store name / footer) ----
export const cacheSettings = (s) => write(KEY_S, s);
export const readSettings = () => read(KEY_S, {});

// ---- Transaction queue ----
export const getQueue = () => read(KEY_Q, []);
const setQueue = (arr) => write(KEY_Q, arr);

export const tempInvoice = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `OFFLINE-${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
};

export const enqueueTx = (body) => {
  if (isMasterView()) throw new Error("Transaksi offline tidak tersedia di master");
  const q = getQueue();
  q.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, body, created_at: new Date().toISOString() });
  setQueue(q);
  return q.length;
};

const removeFromQueue = (id) => setQueue(getQueue().filter((x) => x.id !== id));

export const buildLocalTx = (body, cashier) => {
  const subtotal = body.items.reduce((s, it) => s + Math.max(0, it.qty * it.price - Number(it.discount || 0)), 0);
  const total = Math.max(0, subtotal - (body.discount || 0));
  const paid = body.paid != null ? body.paid : total;
  const creditSale = body.payment_type === 'piutang';
  const methods = Object.fromEntries(readPaymentMethods().map(m => [m.id, m]));
  const creditPayments = (body.payments || []).map(p => {
    const m = methods[p.method];
    const fee = m?.is_cash ? 0 : Math.round(p.amount * Number(m?.fee_percent || 0) / 100);
    return {...p, base: p.amount, fee, amount: p.amount + fee};
  });
  const creditFee = creditPayments.reduce((sum, p) => sum + p.fee, 0);
  return {
    id: null,
    offline: true,
    invoice_no: tempInvoice(),
    items: body.items,
    subtotal,
    discount: body.discount || 0,
    total,
    payment_type: body.payment_type,
    payments: body.payments,
    paid,
    change: Math.max(0, paid - total),
    cashier,
    created_at: new Date().toISOString(),
    note: body.note || null,
    ...(creditSale ? {customer_id: body.customer_id, customer_name: body.customer_name,
      payments: creditPayments, fee: creditFee, grand_total: total + creditFee,
      credit: Math.max(0, total + creditFee - paid), change: 0} : {}),
  };
};

// Keep failed transactions for retry; never silently discard a sale.
export const syncQueue = async (api) => {
  if (isMasterView()) return { ok: 0, fail: 0 };
  const q = getQueue();
  let ok = 0, fail = 0;
  for (const item of q) {
    try {
      await api.post("/transactions", { ...item.body, client_request_id: item.body.client_request_id || item.id });
      removeFromQueue(item.id);
      ok++;
    } catch (e) {
      fail++;
      if (e.response?.status === 401 || e.response?.status === 403) break;
    }
  }
  return { ok, fail };
};
